'use client';

import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { Button, buttonVariants } from './button';
import { Loader2 } from 'lucide-react';
import { cn } from '../../utils';

export interface ConfirmOptions {
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Render the confirm button in destructive styling. */
  destructive?: boolean;
  /**
   * Optional async callback to run on confirmation. While the promise is
   * pending the confirm button shows a loading spinner and both buttons are
   * disabled. If omitted, the dialog resolves `true` immediately (original
   * behaviour).
   */
  onConfirm?: () => void | Promise<void>;
}

type Resolver = (ok: boolean) => void;

interface PendingConfirm extends ConfirmOptions {
  resolve: Resolver;
}

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null);

/**
 * App-wide replacement for `window.confirm`. Returns a Promise<boolean>.
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Delete?', destructive: true })) { ... }
 *
 * Pass `onConfirm` to run an async operation with a built-in loading spinner:
 *
 *   await confirm({ title: 'Delete?', destructive: true, onConfirm: async () => doWork() });
 *
 * The matching <ConfirmProvider> must be mounted above any caller (it lives in App.tsx).
 * Native browser dialogs are banned via ESLint — use this instead.
 */
export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  // Queue so concurrent confirms don't stomp each other.
  const queueRef = useRef<PendingConfirm[]>([]);

  const drain = useCallback(() => {
    const next = queueRef.current.shift();
    setPending(next ?? null);
  }, []);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        const entry: PendingConfirm = { ...opts, resolve };
        setPending((current) => {
          if (current) {
            queueRef.current.push(entry);
            return current;
          }
          return entry;
        });
      }),
    [],
  );

  const handleOpenChange = (open: boolean) => {
    if (!open && pending && !isRunning) {
      pending.resolve(false);
      drain();
    }
  };

  const handleConfirm = async (e: React.MouseEvent) => {
    if (!pending) return;
    if (pending.onConfirm) {
      // Prevent Radix from auto-closing the dialog so we can show loading state.
      e.preventDefault();
      setIsRunning(true);
      try {
        await pending.onConfirm();
        pending.resolve(true);
        drain();
      } catch {
        // Error handling is the caller's responsibility (toast, etc.).
        // Close the dialog so it doesn't get stuck.
        pending.resolve(false);
        drain();
      } finally {
        setIsRunning(false);
      }
    } else {
      pending.resolve(true);
      drain();
    }
  };

  // Auto-format loading text (e.g., "Delete" → "Deleting...")
  const confirmLabel = pending?.confirmLabel ?? 'Confirm';
  const loadingLabel = confirmLabel.endsWith('ing')
    ? confirmLabel
    : `${confirmLabel.replace(/e?$/, '')}ing...`;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={pending !== null} onOpenChange={handleOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.title}</AlertDialogTitle>
            {pending?.description && (
              <AlertDialogDescription>{pending.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRunning}>
              {pending?.cancelLabel ?? 'Cancel'}
            </AlertDialogCancel>
            {pending?.onConfirm ? (
              <Button
                onClick={handleConfirm}
                disabled={isRunning}
                className={cn(
                  pending?.destructive && buttonVariants({ variant: 'destructive' }),
                )}
              >
                {isRunning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {loadingLabel}
                  </>
                ) : (
                  confirmLabel
                )}
              </Button>
            ) : (
              <AlertDialogAction
                onClick={handleConfirm}
                className={cn(
                  pending?.destructive &&
                    buttonVariants({ variant: 'destructive' }),
                )}
              >
                {confirmLabel}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
